import React, { useEffect, useState } from 'react';
import { useContext } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import confetti from '../assets/images/confetti.gif';
import oops from '../assets/images/oops.gif';
import styles from '../assets/styles/DrawDown.module.scss';
import Breadcrumb, { BreadcrumbItem } from '../components/Breadcrumb';
import CreditLinePageTitle from '../components/CreditLine/CreditLinePageTitle';
import Button from '../components/common/Button';
import CustomRadio from '../components/common/CustomRadio';
import Layout from '../components/common/Layout';
import { UserContext } from '../context/UserContext';
import { TRANSFER_OUT_FEE } from '../helpers/constants';
import { formatUSDCurrency } from '../helpers/format';
import {
  getLoanDetails,
  getLoans,
  sendTransferOut,
  sendTransferOutConfirmation,
} from '../service/loan';

const TransferOut = () => {
  const [loans, setLoans] = useState([]);
  const [transferType, setTransferType] = useState('');
  const [error, setError] = useState(null);
  const [confirmationError, setConfirmationError] = useState(null);
  const [transferOutResponse, setTransferOutResponse] = useState(null);
  const [transferOutConfirmationResponse, setTransferOutConfirmationResponse] = useState(null);
  const [loanData, setLoanData] = useState(null);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const { loading, setLoading } = useContext(UserContext);
  const params = useParams();
  const loanId = params.id;
  const loan = loans?.find((loan) => loan.id === loanId);
  const navigate = useNavigate();

  useEffect(() => {
    const loadLoans = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getLoans();

        setLoans(data);
      } catch (e) {
        console.log(e);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadLoans();
  }, []);

  useEffect(() => {
    const loadLoan = async (id) => {
      setLoading(true);
      try {
        const { data } = await getLoanDetails(id);
        setLoanData(data.lineOfCreditDto);
      } catch (e) {
        console.log('error', e);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    if (loans.length > 0) {
      if (loans.find((loan) => loan.id === loanId)) {
        loadLoan(loans?.find((loan) => loan.id === loanId).servicingLoanId);
      }
    }
    setLoading(false);
  }, [loans]);

  useEffect(() => {
    const makeTransferOut = async () => {
      const servicingLoanId = loan.servicingLoanId;
      setLoading(true);
      try {
        const response = await sendTransferOut(servicingLoanId);
        setTransferOutResponse(response.data);
      } catch (e) {
        console.log(e);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    if (loan) makeTransferOut();
  }, [loan]);

  const confirmTransferOut = async () => {
    const servicingLoanId = loan.servicingLoanId;
    const transferReqType = transferType === 'standard' ? 'ach' : 'rtp';
    setLoading(true);
    try {
      const response = await sendTransferOutConfirmation(servicingLoanId, {
        ...transferOutResponse,
        type: transferReqType,
      });
      setTransferOutConfirmationResponse(response.data);
    } catch (e) {
      console.log(e);
      setConfirmationError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setTransferType(e.target.id);
    setError(null);
  };

  const handleSubmit = (e) => {
    if (transferType) {
      setIsConfirmation(true);
    } else {
      setError('Make a selection');
    }
  };
  const disableConfirm = () => {
    if (transferOutResponse) {
      if (transferOutResponse.totalTransferOutGrossAmount == 0) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  };
  const renderContent = () => {
    return !isConfirmation && loanData && transferOutResponse && !loading ? (
      <div className={styles.DrawDown}>
        <Breadcrumb className={styles.DrawDown__breadcrumb}>
          <BreadcrumbItem>
            <Link to={`/dashboard`}>Line of Credit</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>Transfer out</BreadcrumbItem>
        </Breadcrumb>
        <div className={styles.DrawDown__contentBox}>
          <CreditLinePageTitle customClass={styles.pageTitle}>Transfer Out</CreditLinePageTitle>
          <div className={styles.DrawDown__contentBox__details}>
            <p className={styles.DrawDown__contentBox_amountSendOut}>Requested Transfer Amount:</p>
            <p>
              <strong>
                {formatUSDCurrency(transferOutResponse.totalTransferOutGrossAmount, 2, true)}
              </strong>
            </p>
          </div>
          <div className={styles.DrawDown__contentBox__details}>
            <p className={styles.DrawDown__contentBox_amountSendOut}>
              Transfer to Bank Account Ending In:{' '}
            </p>
            <p>
              <strong>***{transferOutResponse.maskedExternalAccountNumber}</strong>
            </p>
          </div>
          <div>
            <p className={styles.DrawDown__contentBox_formTitle}>Transfer Type</p>
            <form
              className={styles.DrawDown__contentBox_form}
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              <div className={styles.labelsWrapper}>
                <label htmlFor="instant">
                  <CustomRadio
                    className={styles.instantRadio}
                    name="transferType"
                    value={'instant'}
                    id="instant"
                    onChange={(e) => handleChange(e)}
                  />
                  <span style={transferType === 'instant' ? { color: '#24163e' } : undefined}>
                    Instant
                  </span>
                </label>
                <label htmlFor="standard">
                  <CustomRadio
                    className={styles.standardRadio}
                    name="transferType"
                    value={'standard'}
                    id="standard"
                    onChange={(e) => handleChange(e)}
                  />
                  <span style={transferType === 'standard' ? { color: '#24163e' } : undefined}>
                    Standard Transfer
                  </span>
                </label>
              </div>
              <div className={styles.DrawDown__contentBox_helperBox}>
                {transferType === 'instant' && (
                  <p className={styles.helperText}>
                    You will be charged{' '}
                    <strong>fee of {formatUSDCurrency(TRANSFER_OUT_FEE, 2, true)}</strong>
                  </p>
                )}
                {transferType === 'standard' && (
                  <p className={styles.helperText}>
                    Your transfer will take between <strong>2-3 working days</strong>
                  </p>
                )}
                {error && <p className={styles.errorText}>{error}</p>}
              </div>
              <Button customClass={styles.DrawDown__button} type="primary" submit>
                Continue
              </Button>
            </form>
          </div>
        </div>
      </div>
    ) : (
      <>
        {transferOutResponse && !loading && (
          <div className={styles.DrawDown}>
            <Breadcrumb className={styles.DrawDown__breadcrumb}>
              <BreadcrumbItem>
                <Link to={`/dashboard`}>Line of Credit</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <span
                  style={{ cursor: 'pointer', color: '#462d7a' }}
                  onClick={() => {
                    setIsConfirmation(false);
                    setTransferType(null);
                    setTransferOutConfirmationResponse(null);
                    setConfirmationError(false);
                  }}
                >
                  Transfer Out
                </span>
              </BreadcrumbItem>
              <BreadcrumbItem>Confirmation</BreadcrumbItem>
            </Breadcrumb>
            <div className={`${styles.DrawDown__contentBox} ${styles.confirmation}`}>
              {!transferOutConfirmationResponse && !confirmationError && (
                <>
                  <CreditLinePageTitle customClass={styles.pageTitle}>
                    Confirmation
                  </CreditLinePageTitle>
                  <div
                    className={`${styles.DrawDown__contentBox_confirmation} ${' '}
                    ${styles.DrawDown__contentBox_transferOut} ${' '}
                    ${styles.transferOutConfirmation}`}
                  >
                    <div>
                      <p>Transfer to Bank Account Ending In:</p>
                      <p>
                        <strong>***{transferOutResponse.maskedExternalAccountNumber}</strong>
                      </p>
                    </div>
                    <div className={styles.DrawDown__contentBox_divider} />

                    <div>
                      <p>Requested Transfer Amount:</p>
                      <p>
                        {' '}
                        <strong>
                          {formatUSDCurrency(
                            transferOutResponse.totalTransferOutGrossAmount,
                            2,
                            true
                          )}
                        </strong>
                      </p>
                    </div>

                    <div>
                      <p>Transfer Fee:</p>
                      <p>
                        <strong>
                          {transferType === 'instant'
                            ? `${formatUSDCurrency(TRANSFER_OUT_FEE, 2, true)}`
                            : '$0'}
                        </strong>
                      </p>
                    </div>
                    <div>
                      <p>
                        <strong>Final Transfer Amount:</strong>
                      </p>
                      <p className={styles.filledBackground}>
                        <strong>
                          {transferType === 'instant'
                            ? formatUSDCurrency(
                                transferOutResponse.totalTransferOutGrossAmount - TRANSFER_OUT_FEE,
                                2,
                                true
                              )
                            : formatUSDCurrency(
                                transferOutResponse.totalTransferOutGrossAmount,
                                2,
                                true
                              )}
                        </strong>
                      </p>
                    </div>
                    {transferType === 'standard' && (
                      <p className={styles.confirmationHelperText}>
                        *Standard Transfer Your transfer will take between{' '}
                        <strong>2-3 working days</strong>
                      </p>
                    )}
                  </div>

                  <Button
                    customClass={styles.DrawDown__button}
                    onClick={() => confirmTransferOut()}
                    type="primary"
                    disabled={disableConfirm()}
                  >
                    Confirm
                  </Button>
                </>
              )}
              {transferOutConfirmationResponse && !confirmationError && (
                <>
                  <CreditLinePageTitle customClass={styles.pageTitle}>Congrats</CreditLinePageTitle>
                  <img src={confetti} alt="Congrats" className={styles.confetti} />
                  <p
                    className={`${styles.DrawDown__contentBox_description} ${styles.congrats_description}`}
                  >
                    Your transfer of{' '}
                    <strong>
                      {transferType === 'instant'
                        ? formatUSDCurrency(
                            transferOutResponse.totalTransferOutGrossAmount - TRANSFER_OUT_FEE,
                            2,
                            true
                          )
                        : formatUSDCurrency(
                            transferOutResponse.totalTransferOutGrossAmount,
                            2,
                            true
                          )}
                    </strong>{' '}
                    was successful
                  </p>

                  <Button
                    customClass={styles.DrawDown__button}
                    onClick={() => {
                      navigate('/dashboard');
                    }}
                    type="primary"
                  >
                    Return Home
                  </Button>
                </>
              )}
              {confirmationError && (
                <>
                  <CreditLinePageTitle customClass={styles.pageTitle}>Oops!</CreditLinePageTitle>
                  <img src={oops} alt="Something went wrong" className={styles.oops} />
                  <p
                    className={`${styles.DrawDown__contentBox_description} ${styles.oops_description}`}
                  >
                    Something went wrong with your transfer of{' '}
                    <strong>
                      {formatUSDCurrency(transferOutResponse.totalTransferOutGrossAmount, 2, true)}
                    </strong>
                  </p>

                  <Button
                    customClass={styles.DrawDown__button}
                    onClick={() => {
                      navigate('/dashboard');
                    }}
                    type="primary"
                  >
                    Return Home
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return <Layout>{renderContent()}</Layout>;
};

export default TransferOut;
